import { EditFileParams, EditFolderParams, Resource } from '@nextforce/api'
import { Input, makeStyles, Text } from '@rneui/themed'
import { t } from 'app/i18l'
import { Image } from 'expo-image'
import { FileFolderIcon } from 'file_folder_repository/views/file_folder_icon'
import moment from 'moment'
import React from 'react'
import { View } from 'react-native'
import { ScrollView } from 'react-native-gesture-handler'

interface Props {
  data: Resource | undefined
  fields: (EditFileParams & EditFolderParams) | undefined
  setFields: React.Dispatch<
    React.SetStateAction<EditFileParams & EditFolderParams>
  >
}

export function EditFileFolderView({ data, fields, setFields }: Props) {
  const styles = useStyles()
  if (!data) {
    return null
  }
  const isFolder = data.type === 'folder'
  const isFile = data.type === 'file'
  const isExternalFile = isFile && data.currentVersion?.externalUrl
  return (
    <View style={styles.detailsContainer}>
      <View style={styles.iconDetailsContainer}>
        <View style={styles.imageContainer}>
          {isFile && data.currentVersion?.thumbnailUrl ? (
            <Image
              source={{ uri: data.currentVersion?.thumbnailUrl }}
              style={styles.image}
              contentFit="contain"
            />
          ) : (
            <FileFolderIcon
              resource={data}
              size={100}
              style={styles.fileFolderIcon}
              externalLinkImageContentFit="contain"
            />
          )}
        </View>
        <ScrollView
          style={styles.itemDetailsContainer}
          contentContainerStyle={styles.scrollViewContainer}
        >
          <InputField
            label={t('name')}
            value={fields?.name}
            onChangeText={text => {
              setFields(prevFields => ({ ...prevFields, name: text }))
            }}
          />
          <InputField
            label={t('description')}
            value={fields?.description}
            onChangeText={text => {
              setFields(prevFields => ({ ...prevFields, description: text }))
            }}
            isDescription
          />
          {isExternalFile ? (
            <InputField
              label={t('externalUrl')}
              value={fields?.externalUrl}
              onChangeText={text => {
                setFields(prevFields => ({ ...prevFields, externalUrl: text }))
              }}
            />
          ) : null}
          <Text style={styles.text}>
            {`${t('uploadedOn')} : ${moment(data.created).format(
              'MMM DD, YYYY'
            )}`}
          </Text>
          {isFolder ? (
            <Text style={styles.text}>
              {`${t('uploadedBy')} : ${data.createdBy?.firstName} ${
                data.createdBy?.lastName
              }`}
            </Text>
          ) : null}
          {isFile ? (
            <Text style={styles.text}>
              {`${t('uploadedBy')} : ${
                data.currentVersion?.uploadedBy.firstName
              } ${data.currentVersion?.uploadedBy.lastName}`}
            </Text>
          ) : null}
        </ScrollView>
      </View>
    </View>
  )
}

function InputField({
  label,
  value,
  onChangeText,
  isDescription,
}: {
  label: string
  value?: string
  isDescription?: boolean
  onChangeText: (text: string) => void
}) {
  const styles = useStyles()
  return (
    <View style={styles.inputFieldRow}>
      <Text>{label}</Text>
      <Input
        value={value}
        onChangeText={onChangeText}
        containerStyle={[
          styles.inputFieldContainer,
          isDescription && styles.descriptionInputField,
        ]}
        multiline={isDescription}
        placeholder={isDescription ? t('enterDescription') : ''}
      />
    </View>
  )
}

const useStyles = makeStyles(theme => ({
  detailsContainer: {
    flex: 1,
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.grey11 : theme.colors.grey7,
  },
  icon: {
    padding: 20,
    backgroundColor: 'black',
    borderRadius: 15,
  },
  title: {
    fontSize: 22,
  },
  text: {
    marginTop: 5,
  },
  iconDetailsContainer: {
    flex: 1,
  },
  itemDetailsContainer: {
    paddingHorizontal: 20,
    flex: 1,
    marginTop: 20,
  },
  image: { width: 200, height: 200 },
  imageContainer: {
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.white : theme.colors.grey6,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  fileFolderIcon: {
    height: 150,
    width: 300,
    justifyContent: 'center',
    alignItems: 'center',
  },
  scrollViewContainer: { paddingBottom: 20 },
  inputFieldContainer: {
    backgroundColor: theme.colors.white,
    borderRadius: 5,
    marginVertical: 5,
    paddingVertical: 5,
  },
  inputFieldRow: { marginBottom: 10 },
  descriptionInputField: { maxHeight: 150, minHeight: 60 },
}))
